import React, { useState, useEffect } from 'react';
import Banner from '../components/common/Banner';
import { apiService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';

const ContactUsPage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    category: 'general'
  });

  useEffect(() => {
    fetchContactData();
  }, []);

  const fetchContactData = async () => {
    try {
      const response = await apiService.getPage('contact');
      if (response.success && response.data.page) {
        setContent(response.data.page.content);
      }
    } catch (err) {
      console.error('Error fetching contact page:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      const response = await apiService.submitContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        category: formData.category
      });

      if (response.success) {
        setSubmitMessage({ type: 'success', text: response.message || 'Your message has been sent successfully!' });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '', category: 'general' });
      }
    } catch (err) {
      setSubmitMessage({ type: 'error', text: err.message || 'Failed to send message. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Fallback values if API data not loaded
  const fallback = {
    hero: { title: 'Contact Us', subtitle: "Get in Touch | We're Here to Help | Connect with FIT", backgroundImage: 'https://images.unsplash.com/photo-1423666639041-f56000c27a9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80' },
    address: { name: 'FIT Group of Institutions', line1: 'NH-58, Partapur Bypass', line2: 'Near Partapur', city: 'Meerut', state: 'Uttar Pradesh', pincode: '250103', country: 'India' },
    phones: { main: '+91 121-2578888', admission: '+91 121-2578889', academic: '+91 121-2578890', placement: '+91 121-2578891' },
    emails: { general: 'info@fitenggmeerut.com', admission: 'admissions@fitenggmeerut.com', academic: 'academics@fitenggmeerut.com', placement: 'placements@fitenggmeerut.com' },
    officeHours: { weekdays: '9:00 AM - 5:00 PM', saturday: '9:00 AM - 2:00 PM', sunday: 'Closed', note: 'Emergency contact available 24/7 for current students through student portal.' },
    socialLinks: { facebook: 'https://www.facebook.com/fitenggmeerut' },
  };
  const address = content?.address || fallback.address;
  const phones = content?.phones || fallback.phones;
  const emails = content?.emails || fallback.emails;
  const officeHours = content?.officeHours || fallback.officeHours;
  const socialLinks = content?.socialLinks || fallback.socialLinks;
  const hero = content?.hero || fallback.hero;
  const mapEmbedUrl = content?.mapEmbedUrl || '';

  return (
    <div className="h-full w-full fade-in">
      <Banner
        title={hero.title || 'Contact Us'}
        subtitle={hero.subtitle || "Get in Touch | We're Here to Help | Connect with FIT"}
        imageSrc={hero.backgroundImage ? (hero.backgroundImage.startsWith('http') ? hero.backgroundImage : getImageUrl(hero.backgroundImage)) : 'https://images.unsplash.com/photo-1423666639041-f56000c27a9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'}
      />

      <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-10 space-y-8 sm:space-y-12">

        {/* Contact Information Cards */}
        <section>
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-3 sm:mb-4">Get in Touch</h2>
            <p className="text-gray-500">Reach out to us through any of the following channels or fill out the contact form below.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Main Office */}
            <div className="bg-white p-6 rounded-xl border border-border_gray text-center">
              <div className="w-16 h-16 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-map-marker-alt text-white text-2xl"></i>
              </div>
              <h3 className="font-bold text-slate mb-3">Main Office</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {address.name || 'FiT Engineering College'}<br/>
                {address.line1 && <>{address.line1}<br/></>}
                {address.line2 && <>{address.line2}<br/></>}
                {address.city && address.state ? `${address.city}, ${address.state}` : ''}{address.pincode ? ` - ${address.pincode}` : ''}<br/>
                {address.country || 'India'}
              </p>
            </div>

            {/* Phone Numbers */}
            <div className="bg-white p-6 rounded-xl border border-border_gray text-center">
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-phone text-white text-2xl"></i>
              </div>
              <h3 className="font-bold text-slate mb-3">Phone Numbers</h3>
              <div className="text-sm text-gray-600 space-y-1">
                {phones.main && <p><strong>Main:</strong> {phones.main}</p>}
                {phones.admission && <p><strong>Admission:</strong> {phones.admission}</p>}
                {phones.academic && <p><strong>Academic:</strong> {phones.academic}</p>}
                {phones.placement && <p><strong>Placement:</strong> {phones.placement}</p>}
                {!phones.main && !phones.admission && <p>Contact numbers coming soon</p>}
              </div>
            </div>

            {/* Email Addresses */}
            <div className="bg-white p-6 rounded-xl border border-border_gray text-center">
              <div className="w-16 h-16 bg-slate rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-envelope text-white text-2xl"></i>
              </div>
              <h3 className="font-bold text-slate mb-3">Email Addresses</h3>
              <div className="text-sm text-gray-600 space-y-1">
                {emails.general && <p><strong>General:</strong> {emails.general}</p>}
                {emails.admission && <p><strong>Admission:</strong> {emails.admission}</p>}
                {emails.academic && <p><strong>Academic:</strong> {emails.academic}</p>}
                {emails.placement && <p><strong>Placement:</strong> {emails.placement}</p>}
                {!emails.general && !emails.admission && <p>Email addresses coming soon</p>}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section>
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl border border-border_gray">
              <h3 className="text-xl font-bold text-crimson mb-6">Send us a Message</h3>

              {submitMessage.text && (
                <div className={`p-4 rounded-lg mb-4 ${submitMessage.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  <i className={`fas fa-${submitMessage.type === 'success' ? 'check-circle' : 'exclamation-circle'} mr-2`}></i>
                  {submitMessage.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text" name="name" value={formData.name} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-crimson outline-none"
                      placeholder="Enter your full name" required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email" name="email" value={formData.email} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-crimson outline-none"
                      placeholder="Enter email address" required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel" name="phone" value={formData.phone} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-crimson outline-none"
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Inquiry Category</label>
                    <select
                      name="category" value={formData.category} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-crimson outline-none"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="admission">Admission</option>
                      <option value="academic">Academic</option>
                      <option value="placement">Training & Placement</option>
                      <option value="research">Research & Development</option>
                      <option value="infrastructure">Infrastructure</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Subject *</label>
                  <input
                    type="text" name="subject" value={formData.subject} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-crimson outline-none"
                    placeholder="Brief subject of your inquiry" required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Message *</label>
                  <textarea
                    rows="5" name="message" value={formData.message} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-crimson outline-none"
                    placeholder="Please describe your inquiry in detail..." required
                  ></textarea>
                </div>

                <div className="flex gap-4">
                  <button type="submit" disabled={submitting}
                    className="bg-crimson hover:bg-red-800 text-white font-bold py-3 px-8 rounded-lg transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                    {submitting ? <><i className="fas fa-spinner fa-spin"></i> Sending...</> : <><i className="fas fa-paper-plane"></i> Send Message</>}
                  </button>
                  <button type="reset"
                    onClick={() => setFormData({ name: '', email: '', phone: '', subject: '', message: '', category: 'general' })}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-8 rounded-lg transition">
                    Reset Form
                  </button>
                </div>
              </form>
            </div>

            {/* Quick Contact & Office Hours */}
            <div className="space-y-6">
              {/* Office Hours */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center">
                  <i className="fas fa-clock text-blue-600 mr-2"></i>
                  Office Hours
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-700 font-semibold">Monday - Friday:</span>
                    <span className="text-blue-600">{officeHours.weekdays || '9:00 AM - 6:00 PM'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700 font-semibold">Saturday:</span>
                    <span className="text-blue-600">{officeHours.saturday || '9:00 AM - 2:00 PM'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700 font-semibold">Sunday:</span>
                    <span className="text-blue-600">{officeHours.sunday || 'Closed'}</span>
                  </div>
                  {officeHours.note && (
                    <div className="border-t border-blue-200 pt-3 mt-4">
                      <p className="text-blue-700 text-xs"><strong>Note:</strong> {officeHours.note}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-white p-6 rounded-xl border border-border_gray">
                <h3 className="text-lg font-bold text-slate mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <button className="w-full bg-gold hover:bg-yellow-600 text-white font-bold py-3 px-4 rounded-lg transition text-sm">
                    <i className="fas fa-download mr-2"></i>
                    Download Admission Brochure
                  </button>
                </div>
              </div>

              {/* Social Media */}
              {(socialLinks.facebook || socialLinks.twitter || socialLinks.linkedin || socialLinks.instagram || socialLinks.youtube) && (
                <div className="bg-white p-6 rounded-xl border border-border_gray">
                  <h3 className="text-lg font-bold text-slate mb-4">Follow Us</h3>
                  <div className="flex gap-3">
                    {socialLinks.facebook && (
                      <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white hover:bg-blue-700 transition">
                        <i className="fab fa-facebook-f"></i>
                      </a>
                    )}
                    {socialLinks.twitter && (
                      <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center text-white hover:bg-sky-600 transition">
                        <i className="fab fa-twitter"></i>
                      </a>
                    )}
                    {socialLinks.linkedin && (
                      <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-white hover:bg-blue-800 transition">
                        <i className="fab fa-linkedin-in"></i>
                      </a>
                    )}
                    {socialLinks.instagram && (
                      <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center text-white hover:bg-pink-600 transition">
                        <i className="fab fa-instagram"></i>
                      </a>
                    )}
                    {socialLinks.youtube && (
                      <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white hover:bg-red-700 transition">
                        <i className="fab fa-youtube"></i>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section>
          <div className="bg-white p-6 rounded-xl border border-border_gray">
            <h3 className="text-xl font-bold text-slate mb-4">Find Us on Map</h3>
            {mapEmbedUrl ? (
              <iframe
                src={mapEmbedUrl}
                className="w-full rounded-lg"
                style={{ height: '400px', border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Campus Location"
              ></iframe>
            ) : (
              <div className="bg-gray-100 rounded-lg aspect-[16/9] flex items-center justify-center">
                <div className="text-center">
                  <i className="fas fa-map text-6xl text-gray-400 mb-4"></i>
                  <p className="text-gray-500 font-semibold">Interactive Map</p>
                  <p className="text-gray-400 text-sm">Campus location and directions</p>
                </div>
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
  );
};

export default ContactUsPage;
